import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/ui";
import { MfaEnrolmentCard } from "@/components/settings/mfa-enrolment-card";
import { MyContactCard } from "@/components/settings/my-contact-card";
import { usePersona } from "@/hooks/use-persona";

export const Route = createFileRoute("/_company/settings/")({
  component: SettingsPage,
});

/**
 * The account settings surface: the person's own contact identity and MFA
 * enrolment, two independent cards behind two independent gates.
 *
 * The SPLIT gate is load-bearing. This page is also the `mfaRequired` landing,
 * and such a session cannot reach the field stack at all — the field middleware
 * answers `GET /field/me` with 403 `mfa_required` until enrolment completes. So
 * the MFA card makes no field-stack call and renders for every session, while
 * the contact card — whose seed and whose PATCH are both field-stack — renders
 * only when the tester probe GRANTED.
 *
 * `usePersona`'s `profile` IS that outcome: it is non-null exactly while
 * `/field/me` succeeded. The branch reads it rather than a grant count because
 * a membership-only admin, an `mfaRequired` tester and a session admitted from
 * the offline snapshot all lack a live profile, and none of them may be shown a
 * form whose every call would fail. A page wired behind one `/field/me` gate
 * instead would re-create the enrolment deadlock the card's own doc-comment
 * warns about.
 */
function SettingsPage() {
  const { t } = useTranslation(["settings"]);
  const { profile } = usePersona();

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        <PageHeader title={t("settings:title")} />
        {profile ? (
          <MyContactCard fullName={profile.full_name} phone={profile.phone ?? ""} />
        ) : null}
        <MfaEnrolmentCard />
      </div>
    </div>
  );
}
